'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TeacherProfile } from 'types/collections';

import { DailyAvailability } from '@components/DaySelectionPanel';
import { Modal } from '@components/Modal';
import { Text } from '@components/TextGroups';

interface NotificationConfirmationModalProps {
  onClose: () => void;
  onConfirm?: () => void;
  mode: 'created' | 'updated';
  schoolName: string | undefined;
  teacher: TeacherProfile | undefined;
  selectedDays: DailyAvailability;
}

export const NotificationConfirmationModal: React.FC<NotificationConfirmationModalProps> = ({
  onClose,
  onConfirm,
  mode,
  schoolName,
  teacher,
  selectedDays,
}) => {
  const { t } = useTranslation('common');

  const name = [teacher?.first_name?.trim(), teacher?.second_name?.trim()].filter(Boolean).join(' ');
  const phoneNumber = teacher?.phone_number?.trim();
  const email = teacher?.email?.trim();
  const selectedNotificationDays =
    (Object.entries(selectedDays) as [keyof DailyAvailability, boolean][])
      .filter(([, isSelected]) => isSelected)
      .map(([day]) => t(`days.${day}`))
      .join(', ') || t('notificationConfirmation.noDays');
  const confirmationContent = t(`notificationConfirmation.${mode}Content`, {
    name,
    schoolName,
  });
  const detailRows = [
    t('notificationConfirmation.detailsTitle'),
    name && `${t('notificationConfirmation.nameLabel')}: ${name}`,
    phoneNumber && `${t('notificationConfirmation.phoneLabel')}: ${phoneNumber}`,
    email && `${t('notificationConfirmation.emailLabel')}: ${email}`,
    `${t('notificationConfirmation.availableDaysLabel')}: ${selectedNotificationDays}`,
  ].filter(Boolean);

  const modalButtons = [
    {
      label: t('notificationConfirmation.confirmButton'),
      onClick: onConfirm || onClose,
      variant: 'primary' as const,
    },
  ];

  return (
    <Modal
      title={t('notificationConfirmation.title')}
      buttons={modalButtons}
      isOpen={true}
      onClose={onClose}>
      <Text>{confirmationContent}</Text>
      <Text>{detailRows.join('\n')}</Text>
    </Modal>
  );
};
